import { FC, useEffect } from 'react';

import { useTranslations } from '@lang';
import { useFormContext, useWatch } from 'react-hook-form';
import { Container } from 'styled-bootstrap-grid';

import { Button, InfoBox, StickyBarContent } from '@common/components';
import Portal from '@common/components/Portal';

import { SpaEntityFormValues } from '@common/components/SpaEntity/SpaEntityForm.schema';
import { EntityTypes } from '@common-types/Entities';
import { AddGuestsForm } from './AddGuestsForm';
import { GuestDetailsProps } from './GuestDetails.types';

export const GuestDetails: FC<GuestDetailsProps> = ({
  spaEntity,
  typeName = 'Spa Treatment',
  entityType,
  stickyBarId,
  onSubmit,
  existingGuests = [],
  handleInvalidConfig,
  guest = null,
  showGenderNeutral,
  isItemFetching,
}) => {
  const { t } = useTranslations();
  const { handleSubmit, formState } = useFormContext<SpaEntityFormValues>();
  const selectedArrivalTime = useWatch({ name: 'arrivalTime' });
  const selectedGuests = useWatch({ name: 'guests' });

  useEffect(() => {
    if (!selectedGuests?.length) {
      handleInvalidConfig();
    }
  }, [selectedGuests, handleInvalidConfig]);

  const getCalculatedPrice = () => ({
    ...selectedArrivalTime.price,
    amount: selectedArrivalTime.price.amount * (selectedGuests?.length ?? 1),
  });

  const getCalculatedDiscount = () =>
    selectedArrivalTime.discount
      ? {
          discountedPrice: {
            ...selectedArrivalTime.discount?.discountedPrice,
            amount:
              selectedArrivalTime.discount?.discountedPrice.amount * (selectedGuests?.length ?? 1),
          },
        }
      : undefined;

  const { availability, extras, minimumGuests, selectionGroups, maximumGuests } = spaEntity || {};
  const timeslotMaxGuests =
    selectedArrivalTime && availability
      ? availability?.find(
          (arrivalTime) => selectedArrivalTime.startDateTime === arrivalTime.startDateTime
        )
      : null;

  const maxGuests = maximumGuests ?? minimumGuests ?? 1;
  const numberAvailable = timeslotMaxGuests?.numberAvailable ?? maxGuests;
  const effectiveMaxGuests = Math.min(numberAvailable, maxGuests);
  const isExtrasAvailable = extras?.length || selectionGroups?.length;
  const hasEnoughQuantity =
    !spaEntity || (!!spaEntity && effectiveMaxGuests >= (minimumGuests ?? 0));

  const Gender = { M: 'male', F: 'female' };
  const defaultGender = spaEntity?.genderSpecific ? Gender[spaEntity.genderSpecific] || '' : '';

  return selectedArrivalTime ? (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Container fluid>
        <AddGuestsForm
          existingGuests={existingGuests}
          guest={guest as any}
          hasEnoughQuantity={hasEnoughQuantity}
          minimumGuests={minimumGuests}
          maximumGuests={effectiveMaxGuests}
          spaEntity={spaEntity}
          showGenderNeutral={showGenderNeutral}
          typeName={typeName}
          isItemFetching={isItemFetching}
          defaultGender={defaultGender}
        />
        {entityType === EntityTypes.TREATMENT && <InfoBox>{t('spa.timeslotsWarning')}</InfoBox>}
      </Container>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={spaEntity.name}
          displayedPrice={getCalculatedPrice() as any}
          discountedPrice={getCalculatedDiscount()}
          button={
            <Button
              isPrimary
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={!hasEnoughQuantity || formState.isSubmitting}
            >
              {!!isExtrasAvailable && t('treatment.customizeTreatmentsCTA')}
              {!isExtrasAvailable && t('treatment.addToBasketCTA')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};
